import { DETECTED_KIND_LABELS, INBOX_AGES, type InboxFilter, plural, REVIEW_STEP_LABELS } from '@hoopoe/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ClipboardCheck, Layers, Lock } from 'lucide-react';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { AutoMatches, dateOf, InboxFilterBar, UnreviewedUploads } from '../components/review/inbox';
import { InboxSamePerson } from '../components/review/same-person';
import { type ReviewQueueItem, useReviewQueue } from '../lib/review';
import { useAutoMatches, useInboxWrite, useUnreviewedUploads } from '../lib/review-inbox';
import { useSchema } from '../lib/schema';

// Review Inbox (PLAN §5 step 7, §6, §6.1; tasks 18, 21, 21a). Sections filtered alike by upload, uploader, document
// type and age: auto-matches a skipped review made, to confirm or detach; documents waiting for review (an upload of
// several files can be reviewed by entity, one card per entity across its files); and uploads added to the graph
// without review, whose unreviewed facts can be checked later. Editors see their own uploads; reviewers and admins
// see everyone's. "Possible matches" (two existing entities that may be one, task 21a and D418) is not filtered:
// those pairs belong to the graph, not to an upload.

const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);

export const Route = createFileRoute('/review/')({
  validateSearch: (s: Record<string, unknown>): InboxFilter => ({
    batch: str(s.batch),
    uploader: str(s.uploader),
    kind: str(s.kind),
    age: (INBOX_AGES as readonly unknown[]).includes(s.age) && s.age !== 'any' ? (s.age as InboxFilter['age']) : undefined,
  }),
  component: ReviewInboxPage,
});

const TOUR = {
  id: 'review-inbox',
  steps: [
    {
      target: '[data-tour="inbox-filters"]',
      title: 'Filter the inbox',
      body: 'Narrow every section to one upload, one uploader, a type of document or an age.',
      learnMore: 'review-inbox',
    },
    {
      target: '[data-tour="inbox-same-person"]',
      title: 'Possible matches',
      body: 'Existing entities that may be one person or organization, best first, each with its certainty and the reasons for it. Answer Same, Different or Not sure; nothing is merged until someone says Same.',
      learnMore: 'match-certainty',
    },
    {
      target: '[data-tour="inbox-queue"]',
      title: 'Waiting for review',
      body: 'Open a file to review it step by step, or review a whole upload by entity: one card per entity across all its files.',
    },
    {
      target: '[data-tour="inbox-unreviewed"]',
      title: 'Added without review',
      body: 'Files whose review was skipped are already in the graph as unreviewed. Check their facts here whenever you have time.',
    },
  ],
};

/** Uploads of several files still at the entity check: each can be reviewed by entity. */
function batchesOf(rows: ReviewQueueItem[]) {
  const by = new Map<string, ReviewQueueItem[]>();
  for (const r of rows) if (!r.comparePending && r.step === 1) by.set(r.batchId, [...(by.get(r.batchId) ?? []), r]);
  return [...by].filter(([, files]) => files.length > 1).map(([id, files]) => ({ id, files }));
}

function ReviewInboxPage() {
  useScreenTour(TOUR);
  const filter = Route.useSearch();
  const navigate = useNavigate({ from: '/review/' });
  const setFilter = (patch: Partial<InboxFilter>) => navigate({ search: s => ({ ...s, ...patch }), replace: true });
  const queue = useReviewQueue(filter);
  const unreviewed = useUnreviewedUploads(filter);
  const auto = useAutoMatches(filter);
  const write = useInboxWrite();
  const { data: snap } = useSchema();
  const rows = queue.data?.documents ?? [];
  const groups = auto.data?.groups ?? [];
  const skipped = unreviewed.data?.documents ?? [];
  const batches = batchesOf(rows);
  const filtered = !!(filter.batch || filter.uploader || filter.kind || filter.age);
  return (
    <div className="space-y-6">
      <PageHeader
        title="Review Inbox"
        description="What the AI found that a person still has to check: files waiting for review, automatic matches, and uploads added without review."
      />
      <Tip id="review-queue" learnMore="review-inbox">
        Review has three steps: entities, then facts and relations, then an AI re-check. Files uploaded with “Review later” go into the
        graph straight away as unreviewed; check them here when you have time.
      </Tip>
      <InboxFilterBar filter={filter} onChange={setFilter} />
      {write.error && (
        <p role="alert" className="text-sm text-danger-foreground">
          {write.error}
        </p>
      )}

      {groups.length > 0 && snap && (
        <section aria-labelledby="inbox-auto" className="space-y-2">
          <h2 id="inbox-auto" className="text-lg font-semibold">
            Auto-matches to confirm <span className="text-sm font-normal text-muted-foreground">({groups.length})</span>
          </h2>
          <p className="text-sm text-muted-foreground">
            These entities were attached to an existing record because the name matched closely. Confirm the match, or detach it into a
            separate entity.
          </p>
          <AutoMatches groups={groups} snap={snap} write={write} />
        </section>
      )}

      {snap && <InboxSamePerson snap={snap} />}

      <section aria-labelledby="inbox-queue" data-tour="inbox-queue" className="space-y-2">
        <h2 id="inbox-queue" className="text-lg font-semibold">
          Waiting for review <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
        </h2>
        {queue.isError && (
          <ErrorState error={queue.error} title="The review queue couldn’t be loaded" onRetry={() => queue.refetch()} compact />
        )}
        {queue.isSuccess && rows.length === 0 && (
          <EmptyState
            icon={ClipboardCheck}
            title="Nothing to review"
            learnMore="review-an-upload"
            action={
              <Link to="/upload" className="text-sm underline underline-offset-2">
                Upload files
              </Link>
            }
          >
            {filtered
              ? 'Nothing matches these filters.'
              : 'Uploads appear here once the AI has read them. Choose “Review now” when uploading to be notified.'}
          </EmptyState>
        )}
        {batches.length > 0 && (
          <ul className="space-y-2" aria-label="Uploads to review by entity">
            {batches.map(b => (
              <li key={b.id} className="flex flex-wrap items-center gap-2 rounded-lg border bg-surface px-3 py-2 text-sm">
                <Layers className="size-4 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <DataText className="font-medium">{b.files[0]?.filename}</DataText> and {plural(b.files.length - 1, 'other file')} from
                  one upload
                </span>
                <Link
                  to="/review/batch/$batchId"
                  params={{ batchId: b.id }}
                  className="rounded-md border px-3 py-1 text-sm font-medium hover:bg-muted"
                >
                  Review {b.files.length} files by entity
                </Link>
              </li>
            ))}
          </ul>
        )}
        {rows.length > 0 && (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm">
              <caption className="sr-only">Documents waiting for review</caption>
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th scope="col" className="p-2 text-start font-medium">
                    File
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Uploaded by
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Step
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Entities to check
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Claims
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Last activity
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b last:border-0" aria-label={r.filename}>
                    <td className="max-w-80 p-2 font-medium">
                      <span className="inline-flex items-center gap-1">
                        <Link to="/review/$id" params={{ id: r.id }} className="truncate underline-offset-2 hover:underline">
                          <DataText>{r.filename}</DataText>
                        </Link>
                        {r.sensitivity === 'confidential' && (
                          <span title="Confidential: processed on Cloudflare only" className="inline-flex">
                            <Lock className="size-3 text-muted-foreground" aria-hidden="true" />
                            <span className="sr-only">Confidential</span>
                          </span>
                        )}
                      </span>
                      <p className="text-xs font-normal text-muted-foreground">{DETECTED_KIND_LABELS[r.detectedKind]}</p>
                    </td>
                    <td className="p-2 text-xs">
                      <DataText>{r.uploader ?? '—'}</DataText>
                    </td>
                    <td className="p-2 text-xs">
                      {r.comparePending ? (
                        'Compare the AI runs'
                      ) : (
                        <>
                          {r.step} · {REVIEW_STEP_LABELS[r.step]}
                          {r.stepStatus === 'in_progress' && <span className="text-muted-foreground"> (started)</span>}
                        </>
                      )}
                    </td>
                    <td className="p-2 text-xs">
                      {r.comparePending ? (
                        <span className="text-muted-foreground">after adoption</span>
                      ) : (
                        <>
                          <DataNumber>{r.entities.pending}</DataNumber> of <DataNumber>{r.entities.total}</DataNumber>
                        </>
                      )}
                    </td>
                    <td className="p-2 text-xs">
                      <DataNumber>{r.claims}</DataNumber>
                    </td>
                    <td className="p-2 text-xs">
                      <DataNumber>{dateOf(r.updatedAt)}</DataNumber>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="inbox-unreviewed" data-tour="inbox-unreviewed" className="space-y-2">
        <h2 id="inbox-unreviewed" className="text-lg font-semibold">
          Added without review <span className="text-sm font-normal text-muted-foreground">({skipped.length})</span>
        </h2>
        {unreviewed.isError && (
          <ErrorState
            error={unreviewed.error}
            title="The unreviewed uploads couldn’t be loaded"
            onRetry={() => unreviewed.refetch()}
            compact
          />
        )}
        {unreviewed.isSuccess && skipped.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {filtered ? 'Nothing matches these filters.' : 'Nothing is waiting: everything added without review has been checked.'}
          </p>
        )}
        {skipped.length > 0 && <UnreviewedUploads rows={skipped} />}
      </section>
    </div>
  );
}
